import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection as firestoreCollection, doc, getDoc, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase';
import {
  Typography, TextField, MenuItem, Alert, Box, Paper, Table, TableHead,
  TableRow, TableCell, TableBody,
} from '@mui/material';

const listMerchandise = httpsCallable(functions, 'listMerchandise');

const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];

// How many of each t-shirt size have been ordered (canceled registrants
// excluded server-side - see listMerchandise). VIEWER_LOCALITY sees only
// their own locality's counts, matching Signups/Payment View.
export default function MerchandisePanel({ scopedLocality }) {
  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS);
  const [viewingId, setViewingId] = useState(null);
  const [sizeOrder, setSizeOrder] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, []);

  const currentOption = viewOptions.find((o) => o.id === viewingId);

  useEffect(() => {
    if (!viewingId) return;
    (async () => {
      setLoading(true);
      setMessage(null);
      try {
        const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
        const [res, confSnap] = await Promise.all([
          listMerchandise(params),
          currentOption?.mode === 'legacy' ? Promise.resolve(null) : getDoc(doc(db, 'conferences', viewingId)),
        ]);
        setItems(res.data?.items || []);
        setSizeOrder(confSnap?.data()?.tshirt?.sizes || []);
      } catch (error) {
        console.error('Failed to load merchandise data:', error);
        setItems([]);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, viewOptions]);

  const sizeCounts = useMemo(() => {
    const counts = new Map();
    for (const item of items) {
      counts.set(item.shirtsize, (counts.get(item.shirtsize) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([size, count]) => ({ size, count }))
      .sort((a, b) => {
        const ai = sizeOrder.indexOf(a.size);
        const bi = sizeOrder.indexOf(b.size);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.size.localeCompare(b.size);
      });
  }, [items, sizeOrder]);

  const localityCounts = useMemo(() => {
    const byLocality = new Map();
    for (const item of items) {
      if (!byLocality.has(item.locality)) byLocality.set(item.locality, 0);
      byLocality.set(item.locality, byLocality.get(item.locality) + 1);
    }
    return [...byLocality.entries()]
      .map(([locality, count]) => ({ locality, count }))
      .sort((a, b) => b.count - a.count);
  }, [items]);

  return (
    <Box sx={{ maxWidth: 700 }}>
      {scopedLocality && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You're viewing merchandise orders for <strong>{scopedLocality}</strong> only.
        </Alert>
      )}
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <TextField
          select fullWidth size="small" label="Viewing conference"
          value={viewingId || ''}
          onChange={(e) => setViewingId(e.target.value)}
        >
          {viewOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
          ))}
        </TextField>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      {loading ? <Typography>Loading...</Typography> : items.length === 0 ? (
        <Typography color="text.secondary">No t-shirts ordered yet for this conference.</Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total t-shirts ordered: <strong>{items.length}</strong>
          </Typography>

          <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>By size</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sizeCounts.map((row) => (
                  <TableRow key={row.size}>
                    <TableCell>{row.size}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>By locality</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Locality</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localityCounts.map((row) => (
                  <TableRow key={row.locality}>
                    <TableCell>{row.locality}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
